import { useState } from 'react';
import { Badge, Button, Card, FileButton, Group, Stack, Text, TextInput } from '@mantine/core';
import { IconUpload } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';
import type { CarouselSendCardInput } from '../../../../api/src/modules/campaigns/schema';

export interface DetectedCarouselCard {
  headerFormat: 'IMAGE' | 'VIDEO';
  bodyVarCount: number;
  /** Ordered button types for this card, e.g. ['QUICK_REPLY'] or ['URL']. */
  buttonTypes: string[];
}

/** Detect an approved carousel template's card structure from Meta's raw
 *  `components` array (returns null when the template isn't a carousel). */
export function detectCarouselCards(t: unknown): DetectedCarouselCard[] | null {
  if (!t || typeof t !== 'object') return null;
  const obj = t as {
    components?: Array<{
      type?: string;
      cards?: Array<{
        components?: Array<{
          type?: string;
          format?: string;
          text?: string;
          buttons?: Array<{ type?: string }>;
        }>;
      }>;
    }>;
  };
  const carousel = obj.components?.find((c) => (c.type ?? '').toUpperCase() === 'CAROUSEL');
  if (!carousel?.cards) return null;
  return carousel.cards.map((card) => {
    const comps = card.components ?? [];
    const header = comps.find((c) => (c.type ?? '').toUpperCase() === 'HEADER');
    const body = comps.find((c) => (c.type ?? '').toUpperCase() === 'BODY');
    const buttons = comps.find((c) => (c.type ?? '').toUpperCase() === 'BUTTONS');
    const bodyVarCount = body?.text ? new Set(body.text.match(/\{\{(\d+)\}\}/g) ?? []).size : 0;
    return {
      headerFormat: ((header?.format ?? 'IMAGE').toUpperCase() as 'IMAGE' | 'VIDEO') || 'IMAGE',
      bodyVarCount,
      buttonTypes: (buttons?.buttons ?? []).map((b) => (b.type ?? '').toUpperCase()),
    };
  });
}

interface CardDraft {
  mediaId: string | null;
  previewUrl: string | null;
  bodyVariables: string[];
  /** Parallel to the detected buttonTypes for this card. */
  buttonValues: string[];
}

function emptyDraft(card: DetectedCarouselCard): CardDraft {
  return {
    mediaId: null,
    previewUrl: null,
    bodyVariables: Array.from({ length: card.bodyVarCount }, () => ''),
    buttonValues: card.buttonTypes.map(() => ''),
  };
}

/**
 * Per-card media/body/button binding for sending an already-approved
 * carousel template — reused by both the campaign wizard (one binding for
 * the whole blast) and the 1:1 chat "Send a template" modal.
 */
export function CarouselSendEditor({
  cards,
  onChange,
}: {
  cards: DetectedCarouselCard[];
  onChange: (input: CarouselSendCardInput[]) => void;
}) {
  const uploadMedia = trpc.whatsapp.templates.uploadSendMedia.useMutation();
  const [drafts, setDrafts] = useState<CardDraft[]>(() => cards.map(emptyDraft));
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);

  const emit = (next: CardDraft[]) => {
    setDrafts(next);
    onChange(
      next.map((d, i) => ({
        header: d.mediaId
          ? {
              kind: cards[i]!.headerFormat === 'IMAGE' ? ('image' as const) : ('video' as const),
              mediaId: d.mediaId,
            }
          : undefined,
        bodyVariables: d.bodyVariables.length > 0 ? d.bodyVariables : undefined,
        buttons:
          cards[i]!.buttonTypes.length > 0
            ? cards[i]!.buttonTypes.map((type, bi) => {
                const value = d.buttonValues[bi];
                if (!value) return null;
                return type === 'URL'
                  ? { index: bi, urlSuffix: value }
                  : { index: bi, payload: value };
              }).filter((b): b is NonNullable<typeof b> => b !== null)
            : undefined,
      })),
    );
  };

  const uploadCardMedia = (index: number, file: File) => {
    setUploadingIndex(index);
    void file
      .arrayBuffer()
      .then((buf) => {
        const bytes = new Uint8Array(buf);
        let bin = '';
        for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
        return uploadMedia.mutateAsync({
          base64: btoa(bin),
          mimeType: file.type,
          filename: file.name,
        });
      })
      .then((r) => {
        const next = [...drafts];
        next[index] = {
          ...next[index]!,
          mediaId: r.mediaId,
          previewUrl: URL.createObjectURL(file),
        };
        emit(next);
        notifications.show({ message: 'Media uploaded', color: 'green' });
      })
      .catch((err: unknown) => {
        notifications.show({
          message: err instanceof Error ? err.message : 'Media upload failed',
          color: 'red',
        });
      })
      .finally(() => setUploadingIndex(null));
  };

  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>
        Carousel cards ({cards.length})
      </Text>
      {cards.map((card, i) => (
        <Card key={i} withBorder p="xs">
          <Group justify="space-between" mb={4}>
            <Badge variant="light">Card {i + 1}</Badge>
          </Group>
          <Stack gap={6}>
            <Group>
              <FileButton
                onChange={(file) => file && uploadCardMedia(i, file)}
                accept={card.headerFormat === 'IMAGE' ? 'image/*' : 'video/*'}
              >
                {(props) => (
                  <Button
                    {...props}
                    size="xs"
                    variant="light"
                    leftSection={<IconUpload size={12} />}
                    loading={uploadingIndex === i}
                  >
                    Upload {card.headerFormat.toLowerCase()}
                  </Button>
                )}
              </FileButton>
              {drafts[i]?.mediaId && (
                <Badge color="green" variant="light">
                  Uploaded ✓
                </Badge>
              )}
            </Group>
            {Array.from({ length: card.bodyVarCount }).map((_, vi) => (
              <TextInput
                key={vi}
                label={`Card body {{${vi + 1}}}`}
                size="xs"
                value={drafts[i]?.bodyVariables[vi] ?? ''}
                onChange={(e) => {
                  const next = [...drafts];
                  const bodyVariables = [...next[i]!.bodyVariables];
                  bodyVariables[vi] = e.currentTarget.value;
                  next[i] = { ...next[i]!, bodyVariables };
                  emit(next);
                }}
              />
            ))}
            {card.buttonTypes.map((type, bi) => (
              <TextInput
                key={bi}
                label={type === 'URL' ? 'URL button suffix' : `${type} payload`}
                size="xs"
                value={drafts[i]?.buttonValues[bi] ?? ''}
                onChange={(e) => {
                  const next = [...drafts];
                  const buttonValues = [...next[i]!.buttonValues];
                  buttonValues[bi] = e.currentTarget.value;
                  next[i] = { ...next[i]!, buttonValues };
                  emit(next);
                }}
              />
            ))}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}
